/**
 * Rehype plugin: group an answer's top-level blocks into heading-led sections.
 *
 * The agent's answer arrives as ONE complete markdown message after server-side verification,
 * so there is nothing incremental to render — the reveal composes its *arrival* rather than
 * faking a stream. Sections are the unit because a heading and the blocks under it are one
 * thought: staggering per block would delay a paragraph behind its own heading, which reads as
 * the page assembling itself rather than as the answer landing.
 *
 * Structure only. The class, the animation and the delay cap live in CSS (see
 * `MdContent`/`typography.css.ts`) — this plugin just says where the seams are and which
 * section is which.
 */

import { HastNode } from './MdTable';

/** The slice of hast this plugin rewrites; avoids a direct `@types/hast` dep (cf. `HastNode`). */
export type RevealRoot = {
  type: 'root';
  children: HastNode[];
};

/** @desc The attribute a section's position is published on; read by CSS to stagger the reveal. */
export const REVEAL_INDEX_ATTRIBUTE = 'data-reveal-index';

const HEADINGS = new Set(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']);

const isHeading = (node: HastNode): boolean =>
  node.type === 'element' && node.tagName !== undefined && HEADINGS.has(node.tagName);

export function rehypeRevealSections() {
  return (tree: RevealRoot): void => {
    const sections: HastNode[][] = [];

    for (const node of tree.children) {
      // A heading opens a section. Anything before the first one — a lead paragraph, a table
      // the answer opens with — is section 0 rather than an orphan outside the stagger.
      if (isHeading(node) || sections.length === 0) sections.push([]);
      sections[sections.length - 1]?.push(node);
    }

    tree.children = sections.map((children, index) => ({
      type: 'element',
      tagName: 'section',
      properties: { [REVEAL_INDEX_ATTRIBUTE]: String(index) },
      children,
    }));
  };
}
